// DeliveryInfo.tsx
import React, { useEffect } from "react";
import { MapPin } from "lucide-react";
import defaultAvatar from "../../../../../assets/images/default_avatar.jpg";
import arrow from "../../../../../assets/images/arrow.png";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";

interface DeliveryInfoProps {
  address?: string;
  pincode?: string;
  orderDate?: string;
  orderStatus?: string;
  patientId?: string;
  clinicDetails?: {
    clinicName?: string;
    clinicProfileImage?: string | null;
    clinicSupervisorName?: string;
    clinicAddress?: string;
    clinicPincode?: string;
    clinicMobileNumber?: string;
  } | null;
}

interface PatientDetails {
  name: string;
  mobile_number: string;
  profile_image?: string;
}

const DeliveryInfo: React.FC<DeliveryInfoProps> = ({
  address,
  pincode,
  orderDate,
  orderStatus,
  patientId,
  clinicDetails,
}) => {
  const [patientDetails, setPatientDetals] =
    React.useState<PatientDetails | null>(null);

  const formatStatus = (status?: string) => {
    if (!status) return "Processing";
    return status
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    if (!patientId) return;

    const getPatientDetails = async () => {
      try {
        const response = await AdminOrderService.getAdminPatientDetailsById(
          patientId
        );
        setPatientDetals(response.data);
      } catch (error) {
        console.log(error);
      }
    };

    getPatientDetails();
  }, [patientId]);

  return (
    <>
      {/* Delivery Address */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <MapPin className="w-4 h-4 text-gray-500 mr-1" />
          <span className="text-sm font-medium text-gray-700">
            Delivery Address
          </span>
        </div>
        <p className="text-sm text-gray-600 ml-5">
          {address || "Address not specified"}
          <br />
          {pincode && `Pin Code: ${pincode}`}
        </p>
      </div>

      {/* Delivery Agent */}
      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between w-full">
        {/* Ordered By */}
        <div className="mb-4 xl:mb-0 w-full xl:w-auto">
          <div className="mt-3 mb-4">
            <p className="text-gray-700 mb-2">Ordered by</p>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                <img
                  src={
                    patientDetails?.profile_image
                      ? patientDetails.profile_image
                      : defaultAvatar
                  }
                  alt="user"
                  className="w-8 h-8 rounded-full"
                />
              </div>
              <div>
                <p className="font-medium">{patientDetails?.name}</p>
                <p className="text-gray-500 text-xs">
                  {patientDetails?.mobile_number}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div className="hidden xl:block">
          <img src={arrow} alt="arrow" className="w-11 h-4" />
        </div>

        {/* Assign to Clinic */}
        <div className="w-full xl:w-auto">
          <div className="mt-3 mb-4">
            <div className="flex items-center">
              {clinicDetails ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                    <img
                      src={
                        clinicDetails.clinicProfileImage
                          ? clinicDetails.clinicProfileImage
                          : defaultAvatar
                      }
                      alt="clinic"
                      className="w-8 h-8 rounded-full"
                    />
                  </div>
                  <div>
                    <p className="font-medium">{clinicDetails.clinicName}</p>
                    {clinicDetails.clinicSupervisorName && (
                      <p className="text-gray-500 text-xs">
                        {clinicDetails.clinicSupervisorName}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="bg-gray-100 p-2 rounded text-gray-700">
                  No clinic assigned
                </div>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {orderDate
                ? `Order Date: ${orderDate}`
                : "Date will be generated once order is processed"}
            </p>
          </div>
        </div>
      </div>

      {/* Order status display */}
      <div className="mb-4">
        <span className="ml-2 font-medium text-blue-500">
          <p className="text-gray-700">
            Status:
            <span className="ml-2 font-medium text-blue-500">
              {formatStatus(orderStatus)}
            </span>
          </p>
        </span>
        <p className="text-gray-500 text-xs">
          {orderDate && (
            <p className="text-gray-500 text-xs">Order Date: {orderDate}</p>
          )}
        </p>
      </div>
    </>
  );
};

export default DeliveryInfo;
